import { createAction } from "@reduxjs/toolkit";
import * as schemaCalendars from "~/schemas/schema.calendar";
import { arrayOfCalendarEvents } from "~/schemas/schema.calendarEvents.js";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch.jsx";
import { param } from "~/utils/url_helper.jsx";
import * as trashActions from "./actions.trash.jsx";

export const fetchCalendar = (id) => {
  const url = `${Angie.api_url}/calendars/${id}`;
  return {
    types: [actionTypes.FETCH_CALENDAR_LOADING, actionTypes.FETCH_CALENDAR_SUCCESS, actionTypes.FETCH_CALENDAR_ERROR],
    callAPI: () =>
      fetch({
        url: url,
      }),
    payload: {
      id,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
    cache: { key: url },
  };
};

export const fetchCalendars = () => {
  const url = `${Angie.api_url}/calendars`;
  return {
    types: [
      actionTypes.FETCH_CALENDARS_LOADING,
      actionTypes.FETCH_CALENDARS_SUCCESS,
      actionTypes.FETCH_CALENDARS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: url,
      }),
    payload: {
      schema: schemaCalendars.arrayOfCalendars,
    },
    cache: { key: url },
  };
};

export const addCalendar = (attributes) => {
  return {
    types: [actionTypes.ADD_CALENDAR_LOADING, actionTypes.ADD_CALENDAR_SUCCESS, actionTypes.ADD_CALENDAR_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      attributes,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
  };
};

export const addExternalCalendar = (attributes) => {
  return {
    types: [
      actionTypes.ADD_EXTERNAL_CALENDAR_LOADING,
      actionTypes.ADD_EXTERNAL_CALENDAR_SUCCESS,
      actionTypes.ADD_EXTERNAL_CALENDAR_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/create-external`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      attributes,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
  };
};

export const editCalendar = (id, attributes) => {
  return {
    types: [actionTypes.EDIT_CALENDAR_LOADING, actionTypes.EDIT_CALENDAR_SUCCESS, actionTypes.EDIT_CALENDAR_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${id}`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      id,
      attributes,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
  };
};

export const moveToTrash = (id) => {
  const objectType = `Calendar`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_CALENDAR_TO_TRASH_LOADING,
      actionTypes.MOVE_CALENDAR_TO_TRASH_SUCCESS,
      actionTypes.MOVE_CALENDAR_TO_TRASH_ERROR,
    ],
    payload: {
      id,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
  };
};

export const restoredFromTrash = (id) => {
  const objectType = `Calendar`;
  return {
    ...trashActions.restoredFromTrash(objectType, id),
    types: [
      actionTypes.RESTORED_CALENDAR_FROM_TRASH_LOADING,
      actionTypes.RESTORED_CALENDAR_FROM_TRASH_SUCCESS,
      actionTypes.RESTORED_CALENDAR_FROM_TRASH_ERROR,
    ],
    payload: {
      id,
      schema: {
        single: schemaCalendars.calendar,
      },
    },
  };
};

export const deleteCalendar = (id) => {
  const objectType = `Calendar`;
  return {
    ...trashActions.permanentlyDelete(objectType, id),
    types: [
      actionTypes.DELETE_CALENDAR_LOADING,
      actionTypes.DELETE_CALENDAR_SUCCESS,
      actionTypes.DELETE_CALENDAR_ERROR,
    ],
    payload: {
      id,
    },
  };
};

export const setCalendarFromEventStream = createAction(actionTypes.SET_CALENDAR_FROM_EVENT_STREAM);
export const deleteCalendarFromEventStream = createAction(actionTypes.DELETE_CALENDAR_FROM_EVENT_STREAM);

export const loadCalendars = createAction(actionTypes.LOAD_CALENDARS, (payload) => {
  if (Array.isArray(payload)) {
    return {
      payload: payload.reduce((prev, next) => {
        prev[next.id] = next;
        return prev;
      }, {}),
    };
  }
  return {
    payload: payload,
  };
});

export const fetchCalendarAssigments = (params) => {
  const url = `${Angie.api_url}/projects/calendar-events?${param(params)}`;
  return {
    types: [
      actionTypes.FETCH_CALENDAR_ASSIGMENTS_LOADING,
      actionTypes.FETCH_CALENDAR_ASSIGMENTS_SUCCESS,
      actionTypes.FETCH_CALENDAR_ASSIGMENTS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: url,
      }),
    payload: {
      schema: schemaCalendars.calendarAssigments,
    },
    cache: {
      key: url,
    },
  };
};

export const refreshExternalCalendar = (id) => {
  return {
    types: [
      actionTypes.REFRESH_EXTERNAL_CALENDAR_LOADING,
      actionTypes.REFRESH_EXTERNAL_CALENDAR_SUCCESS,
      actionTypes.REFRESH_EXTERNAL_CALENDAR_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${id}/refresh`,
        method: `post`,
      }),
    payload: {
      id,
      schema: {
        calendar: schemaCalendars.calendar,
        events: arrayOfCalendarEvents,
      },
    },
  };
};
